import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eyebrow } from "../components/Reveal";
import { WellbeingWheel } from "../components/WellbeingWheel";

const LINES = ["A more precise way", "to understand", "your health."];

const MaskedLine = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  return (
    <section className="relative grain bg-ivory overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-36 pb-20 lg:pt-44 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.img
            src="/assets/logo-mark.png"
            alt="InSight Metabolics emblem"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.0, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="h-16 sm:h-20 w-auto mb-8"
          />
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <Eyebrow>A Precision Metabolic &amp; Lifestyle Clinic</Eyebrow>
          </motion.div>
          <h1 className="mt-7 font-serif font-medium text-forest text-4xl sm:text-5xl lg:text-[64px] leading-[1.08] tracking-tight max-w-3xl">
            {LINES.map((l, i) => (
              <MaskedLine key={l} delay={0.25 + i * 0.14}>
                {i === 2 ? <em className="not-italic text-mist">{l}</em> : l}
              </MaskedLine>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 text-base sm:text-lg text-inkmuted leading-relaxed max-w-xl"
          >
            Personalised metabolic and lifestyle medicine for people who want to understand why they aren&rsquo;t
            feeling or functioning at their best&mdash;and what they can realistically do about it.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/health-profile"
              data-testid="hero-cta-profile"
              className="inline-flex justify-center items-center rounded-full bg-forest text-ivory px-9 py-4 text-sm font-semibold tracking-wide border border-gold/40 hover:bg-forestdeep transition-colors duration-300"
            >
              Start Your Health Journey
            </Link>
            <button
              data-testid="hero-cta-approach"
              onClick={() => document.getElementById("approach")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex justify-center items-center rounded-full px-9 py-4 text-sm font-semibold tracking-wide text-forest border border-forest/30 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              How It Works
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-gold/50 pointer-events-none" aria-hidden="true" />
            <div className="relative p-2 sm:p-4">
              <WellbeingWheel />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
